import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

/**
 * 音声キュー状態の列挙型
 */
export enum AudioQueueState {
  IDLE = 'IDLE',
  PLAYING_RESPONSE = 'PLAYING_RESPONSE',
  PLAYING_FILLER = 'PLAYING_FILLER',
  INTERRUPTED = 'INTERRUPTED',
}

/**
 * 音声キューステートマシンサービス
 *
 * @description
 * ロールプレイセッション中の音声キュー再生状態を管理する有限ステートマシン。
 * 4つの状態（IDLE、PLAYING_RESPONSE、PLAYING_FILLER、INTERRUPTED）と
 * それらの間の許可された遷移を定義し、無効な遷移を防止する。
 *
 * @responsibilities
 * - 音声キュー状態の遷移ロジック管理
 * - 状態遷移バリデーション（許可された遷移のみ実行）
 * - 状態変化のObservable発行
 * - 割り込み処理時の状態遷移（PLAYING_FILLER → INTERRUPTED → PLAYING_RESPONSE）
 *
 * @dependencies
 * - RxJS BehaviorSubject（状態ストリーム管理）
 *
 * @example
 * ```typescript
 * constructor(private queueSM: AudioQueueStateMachineService) {}
 *
 * ngOnInit() {
 *   // 状態変化を購読
 *   this.queueSM.getCurrentState().subscribe(state => {
 *     console.log('現在のキュー状態:', state);
 *   });
 *
 *   // 音声再生開始
 *   if (this.queueSM.transition(AudioQueueState.PLAYING_RESPONSE)) {
 *     console.log('応答音声の再生を開始しました');
 *   }
 *
 *   // フィラー音声の割り込み
 *   if (this.queueSM.transition(AudioQueueState.INTERRUPTED)) {
 *     console.log('フィラー音声を中断しました');
 *   }
 * }
 * ```
 *
 * @see design.md AudioQueueStateMachineService section
 * @see requirements.md 要件4.4: 音声キュー状態の別FSM実装
 */
@Injectable({
  providedIn: 'root',
})
export class AudioQueueStateMachineService {
  /**
   * 現在の状態を保持するBehaviorSubject
   */
  private currentState$ = new BehaviorSubject<AudioQueueState>(
    AudioQueueState.IDLE,
  )

  /**
   * 状態遷移テーブル
   * 各状態から遷移可能な状態のリスト
   *
   * 遷移ルール:
   * - IDLE: 再生開始（PLAYING_RESPONSE / PLAYING_FILLER）のみ可能
   * - PLAYING_RESPONSE: 再生完了（IDLE）または次の応答再生（PLAYING_RESPONSE）が可能
   * - PLAYING_FILLER: 再生完了（IDLE）または割り込み（INTERRUPTED）が可能
   * - INTERRUPTED: 応答音声の再生（PLAYING_RESPONSE）のみ可能
   */
  private readonly transitionTable: Record<AudioQueueState, AudioQueueState[]> =
    {
      [AudioQueueState.IDLE]: [
        AudioQueueState.PLAYING_RESPONSE,
        AudioQueueState.PLAYING_FILLER,
      ],
      [AudioQueueState.PLAYING_RESPONSE]: [
        AudioQueueState.IDLE,
        AudioQueueState.PLAYING_RESPONSE,
      ],
      [AudioQueueState.PLAYING_FILLER]: [
        AudioQueueState.IDLE,
        AudioQueueState.INTERRUPTED,
      ],
      [AudioQueueState.INTERRUPTED]: [AudioQueueState.PLAYING_RESPONSE],
    }

  /**
   * 現在の音声キュー状態を取得
   *
   * @returns 状態のObservableストリーム
   */
  getCurrentState(): Observable<AudioQueueState> {
    return this.currentState$.asObservable()
  }

  /**
   * 新しい状態へ遷移
   *
   * @param newState 遷移先の状態
   * @returns 遷移成功ならtrue、無効遷移ならfalse
   * @throws Error 遷移失敗時にログ出力
   */
  transition(newState: AudioQueueState): boolean {
    const currentState = this.currentState$.value

    if (!this.canTransition(currentState, newState)) {
      console.warn(
        `[AudioQueueStateMachine] 無効な状態遷移を検出: ${currentState} → ${newState}`,
      )
      return false
    }

    this.currentState$.next(newState)
    console.log(
      `[AudioQueueStateMachine] 状態遷移: ${currentState} → ${newState}`,
    )
    return true
  }

  /**
   * 遷移が許可されているかチェック
   *
   * @param from 現在の状態
   * @param to 遷移先の状態
   * @returns 許可されていればtrue
   */
  canTransition(from: AudioQueueState, to: AudioQueueState): boolean {
    const allowedTransitions = this.transitionTable[from]
    return allowedTransitions.includes(to)
  }

  /**
   * 状態をリセット（IDLE状態に戻す）
   */
  reset(): void {
    this.currentState$.next(AudioQueueState.IDLE)
    console.log('[AudioQueueStateMachine] 状態をIDLEにリセットしました')
  }
}
